import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DonutStatCard from "../DonutStatCard";
import axiosInstance from "../../services/axios";
import { getCurrentClientId } from "../../config/villageConfig";
import arrowDownwardIcon from "../../assets/icons/arrow_downward.svg";
import currencyRupeeIcon from "../../assets/icons/currency_rupee.svg";
import flagIcon from "../../assets/icons/flag.svg";
import homeIcon from "../../assets/icons/home.svg";
import waterIcon from "../../assets/icons/water.svg";
import rentIcon from "../../assets/icons/rent.svg";
import lowPriorityIcon from "../../assets/icons/low_priority.svg";
import { formatPercent } from "../../utils/numUtils";

// Client-specific income data (only for clientId 1)
const CLIENT_1_INCOME_DATA = [
  { title: "१५ वित्त आयोग", label: "१५ वित्त आयोग", received_amount: 6533394, spent_amount: 5446945, percentage: 78.24, amount: 6533394, color: "#06b6d4" },
  { title: "राज्य शासन अनुदान", label: "राज्य शासन अनुदान", received_amount: 351655, spent_amount: 351655, percentage: 4.21, amount: 351655, color: "#8b5cf6" },
  { title: "घरपट्टी कर", label: "घरपट्टी कर", received_amount: 1141931, spent_amount: 627541, percentage: 13.68, amount: 1141931, color: "#f97316" },
  { title: "पाणी शुल्क", label: "पाणी शुल्क", received_amount: 221100, spent_amount: 67300, percentage: 2.65, amount: 221100, color: "#10b981" },
  { title: "भाडे", label: "भाडे", received_amount: 2400, spent_amount: 0, percentage: 0.03, amount: 2400, color: "#f43f5e" },
  { title: "इतर", label: "इतर", received_amount: 100001, spent_amount: 4545, percentage: 1.20, amount: 100001, color: "#f59e0b" },
];

// Default empty data for other clients
const DEFAULT_CHART_DATA = [
  { title: "१५ वित्त आयोग", label: "१५ वित्त आयोग", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#06b6d4" },
  { title: "राज्य शासन अनुदान", label: "राज्य शासन अनुदान", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#8b5cf6" },
  { title: "घरपट्टी कर", label: "घरपट्टी कर", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#f97316" },
  { title: "पाणी शुल्क", label: "पाणी शुल्क", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#10b981" },
  { title: "भाडे", label: "भाडे", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#f43f5e" },
  { title: "इतर", label: "इतर", received_amount: 0, spent_amount: 0, percentage: 0, amount: 0, color: "#f59e0b" },
];

const ICON_CONFIG = [
  { icon: currencyRupeeIcon, color: "#14B8A6" },
  { icon: flagIcon, color: "#3B82F6" },
  { icon: homeIcon, color: "#6366F1" },
  { icon: waterIcon, color: "#EC4899" },
  { icon: rentIcon, color: "#F59E0B" },
  { icon: lowPriorityIcon, color: "#FACC15" },
];

const formatAmount = (amount = 0, lang = "en") => {
  const formatted = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(
    Math.round((amount + Number.EPSILON) * 100) / 100
  );
  if (lang === "mr") {
    const digits = "०१२३४५६७८९";
    return "₹" + formatted.replace(/[0-9]/g, (d) => digits[parseInt(d, 10)]);
  }
  return "₹" + formatted;
};

const StatusCard = ({ card, index, t, onViewDetails }) => (
  <div
    key={index}
    className="bg-white border border-[#DDDDDD] rounded-lg flex flex-col items-start px-4 py-3 gap-2.5"
  >
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center"
      style={{ backgroundColor: card.iconColor }}
    >
      <img src={card.icon} alt="" className="w-6 h-6" style={{ filter: "brightness(0) invert(1)" }} />
    </div>

    <div className="text-xl max-sm:text-lg font-semibold text-[#0A0A0A]">{card.label}</div>

    <div className="w-full flex flex-col gap-1">
      <div className="flex justify-between w-full">
        <span className="text-base font-medium text-[#0A0A0A]">{t("incomeExpenditureUI.received")}</span>
        <span className="text-base font-medium text-[var(--color-primary)]">{card.received}</span>
      </div>
      <div className="flex justify-between w-full">
        <span className="text-base font-medium text-[#0A0A0A]">{t("incomeExpenditureUI.spent")}</span>
        <span className="text-base font-medium text-[#B7131A]">{card.spent}</span>
      </div>
      <div className="flex justify-between w-full">
        <span className="text-base font-medium text-[#0A0A0A]">{t("incomeExpenditureUI.balance")}</span>
        <span className="text-base font-medium text-[#212121]">{card.balance}</span>
      </div>
    </div>

    {index === 0 ? (
      <button
        onClick={onViewDetails}
        className="w-full h-10 px-6 border border-[var(--color-primary)] rounded-lg text-[var(--color-primary)] font-medium text-sm hover:bg-[var(--color-primary)] hover:text-white transition-colors"
      >
        {t("incomeExpenditure.viewDetails")}
      </button>
    ) : (
      <div className="w-full h-10" />
    )}
  </div>
);

const IncomeSource = ({ data, totalIncome, selectedYear }) => {
  const { t, i18n } = useTranslation();
  const clientId = getCurrentClientId();
  // Use client-specific data only for clientId 1, otherwise use API data or empty defaults
  const defaultData = clientId === 1 ? CLIENT_1_INCOME_DATA : DEFAULT_CHART_DATA;
  const rawData = data || defaultData;
  const lang = i18n.language === "en" ? "en" : "mr";
  const [incomeReportLink, setIncomeReportLink] = useState("");
  console.log("Raw data:", rawData);

  const totalReceived = rawData.reduce((sum, item) => sum + (parseFloat(item.received_amount || item.received) || 0), 0);
  const chartData = rawData.map((item, index) => {
    const received = parseFloat(item.received_amount || item.received) || 0;
    const percentage = totalReceived > 0 ? (received / totalReceived) * 100 : 0;
    const config = ICON_CONFIG[index] || ICON_CONFIG[0];

    return {
      ...item,
      value: percentage,
      color: config.color,
      received: received,
      label: item.title || item.label,
    };
  });


  const statusCards = chartData.map((item, index) => {
    const percentage = parseFloat(item.percentage) || 0;
    const received = parseFloat(item.received_amount) || 0;
    const spent = parseFloat(item.spent_amount) || 0;
    const amount = parseFloat(item.amount) || 0;
    const balance = received - spent;
    const config = ICON_CONFIG[index] || ICON_CONFIG[0];
    return {
      label: item.title,
      percentage: formatPercent(percentage, lang, { decimals: 2 }),
      amount: formatAmount(amount, lang),
      received: formatAmount(received, lang),
      spent: formatAmount(spent, lang),
      balance: formatAmount(balance, lang),
      icon: config.icon,
      iconColor: config.color,
    };
  });

  // Fetch income report document
  useEffect(() => {
    const fetchIncomeReport = async () => {
      try {
        const response = await axiosInstance.get("/financial-report-docs", {
          params: { tag: "Income Report", financial_year: selectedYear?.id }
        });
        const data = response.data || [];
        if (data && data.length > 0) {
          setIncomeReportLink(data[0].pdf_file);
        }
      } catch (error) {
        console.error("Failed to fetch income report:", error);
      }
    };
    fetchIncomeReport();
  }, [selectedYear]);

  const handleViewDetails = () => {
    if (incomeReportLink) {
      window.open(incomeReportLink, '_blank', 'noopener,noreferrer');
    } else {
      console.warn("No income report link available");
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="rounded-md">
        {/* Header */}
        <div
          className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 rounded-md"
          style={{ background: "linear-gradient(135deg, #00C950 0%, #006D29 100%)" }}
        >
          <div className="flex items-center gap-3 justify-center sm:justify-start">
            <div className="w-9 h-9 rounded-full flex items-center justify-center">
              <img src={arrowDownwardIcon} alt="" />
            </div>
            <span className="text-white text-xl sm:text-2xl font-semibold">
              {t("incomeExpenditure.incomeSource") || "Income"}
            </span>
          </div>
          <span className="text-white font-bold text-lg sm:text-xl text-center sm:text-right">
            {formatAmount(totalIncome || totalReceived, lang)}
          </span>
        </div>

        {/* Content */}
        <div className="bg-white mt-8 rounded-b-md">
          <div className="mb-8">
            <DonutStatCard chartData={chartData} enableAnimation animationDuration={1500} />
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {statusCards.map((item, idx) => {
              const config = ICON_CONFIG[idx] || ICON_CONFIG[0];
              const label = item.label;
              const amount = item.amount;
              const percentage = item.percentage;
              return (
                <div
                  key={idx}
                  className="flex flex-row items-center px-4 py-2 gap-3 w-full bg-white rounded-lg border border-[#DDDDDD]"
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0"
                    style={{ backgroundColor: config.color }}
                  >
                    <img
                      src={config.icon}
                      alt=""
                      className="w-5 h-5 md:w-6 md:h-6"
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  </div>
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <div className="text-sm md:text-base font-semibold text-[#0A0A0A] truncate w-full">
                      {label}
                    </div>
                    <div className="text-base font-medium text-[var(--color-primary)] truncate w-full">
                      {amount}
                    </div>
                    <div className="text-sm md:text-base font-semibold text-[#0A0A0A]">
                      {percentage}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div>
            <h3 className="text-[#0A0A0A] text-3xl max-sm:text-2xl font-semibold">
              {t("incomeExpenditureUI.currentStatus")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {statusCards.map((card, idx) => (
                <StatusCard key={idx} card={card} index={idx} t={t} onViewDetails={handleViewDetails} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeSource;
